/**
 * Scan Insights — aggregate interesting factoids from a user's full report history.
 *
 * Each insight has:
 *   id      — stable key for React rendering
 *   label   — short heading
 *   value   — prominent display value
 *   sub     — secondary context line
 *   color   — tailwind text-color class for the value
 */

import type { Report } from '@/lib/reports-api';

export interface Insight {
  id: string;
  label: string;
  value: string;
  sub?: string;
  color?: string; // tailwind text-* class
}

interface FindingLike {
  severity: string;
  category?: string;
  file?: string;
}

function toFindings(raw: unknown[]): FindingLike[] {
  return (raw ?? []).filter(Boolean) as FindingLike[];
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function repoName(target: string, maxLen = 26): string {
  const name = target
    .replace(/^https?:\/\/(github\.com\/)?/, '')
    .split('/')
    .slice(0, 2)
    .join('/');
  return name.length > maxLen ? name.slice(0, maxLen) + '…' : name;
}
/** Truncate a string to maxLen chars, appending ellipsis if needed */
function trunc(s: string, maxLen = 18): string {
  return s.length > maxLen ? s.slice(0, maxLen) + '\u2026' : s;
}
function modeEntry<T>(arr: T[]): [T, number] | undefined {
  const counts = new Map<T, number>();
  for (const v of arr) counts.set(v, (counts.get(v) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0] as [T, number] | undefined;
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  'github-repo': 'GitHub',
  npm: 'npm',
  pypi: 'PyPI',
  mcp: 'MCP',
  agent: 'AI Agent',
};

/**
 * Compute an ordered list of interesting insights from all completed reports.
 * Returns empty array when no completed scans exist yet.
 */
export function computeInsights(reports: Report[]): Insight[] {
  const completed = reports.filter((r) => r.status === 'completed');
  if (completed.length === 0) return [];

  const allFindings = completed.flatMap((r) => toFindings(r.findings));

  // ── Basic sums ─────────────────────────────────────────────────────────
  const totalLines = completed.reduce((s, r) => s + ((r.metadata.linesScanned as number) || 0), 0);
  const totalFiles = completed.reduce((s, r) => s + ((r.metadata.filesScanned as number) || 0), 0);
  const totalFindings = allFindings.length;
  const criticalCount = allFindings.filter((f) => f.severity === 'critical').length;
  const highCount = allFindings.filter((f) => f.severity === 'high').length;

  // ── Category / file hotspots ───────────────────────────────────────────
  const topCategory = modeEntry(allFindings.map((f) => f.category).filter(Boolean) as string[]);
  const topFile = modeEntry(allFindings.map((f) => f.file).filter(Boolean) as string[]);

  // ── Unique repos & type mix ────────────────────────────────────────────
  const uniqueTargets = new Set(completed.map((r) => r.target)).size;
  const topTypeEntry = modeEntry(completed.map((r) => r.targetType));

  // ── Scans this calendar month ─────────────────────────────────────────
  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);
  const completedThisMonth = completed.filter((r) => new Date(r.scannedAt) >= monthStart);
  const uniqueTargetsThisMonth = new Set(completedThisMonth.map((r) => r.target)).size;

  // ── Risk extremes ─────────────────────────────────────────────────────
  const scored = completed.filter(
    (r) => typeof (r.scores as Record<string, unknown>).aivss === 'number'
  );
  const sorted = [...scored].sort(
    (a, b) =>
      ((b.scores as Record<string, unknown>).aivss as number) -
      ((a.scores as Record<string, unknown>).aivss as number)
  );
  const riskiest = sorted[0];
  const safestScored = [...scored]
    .filter((r) => r.recommendation === 'SAFE')
    .sort(
      (a, b) =>
        ((a.scores as Record<string, unknown>).aivss as number) -
        ((b.scores as Record<string, unknown>).aivss as number)
    )[0];

  // ── Scan duration ─────────────────────────────────────────────────────
  const durations = completed.map((r) => (r.metadata.duration as number) || 0).filter((d) => d > 0);
  const avgDuration = durations.length
    ? durations.reduce((a, b) => a + b, 0) / durations.length
    : 0;

  // ── VendorGuard signals ───────────────────────────────────────────────
  type VG = Record<string, Record<string, unknown>>;
  const vg = (r: (typeof completed)[0]) => (r.vendorGuard as VG | undefined) ?? null;

  const hardcodedIPCount = completed.filter(
    (r) => vg(r)?.code_forensics?.hardcoded_ips === true
  ).length;

  const allDangerousCalls = completed.flatMap(
    (r) => (vg(r)?.code_forensics?.dangerous_calls as string[]) ?? []
  );
  const topDangerousCall = modeEntry(allDangerousCalls);

  const openssfScores = completed
    .map((r) => (vg(r)?.security_hygiene?.openssf_score as number | null) ?? null)
    .filter((s): s is number => s !== null && s > 0);
  const avgOpenssf = openssfScores.length
    ? openssfScores.reduce((a, b) => a + b, 0) / openssfScores.length
    : null;

  // ── Severities across scans for the SAFE percentage ──────────────────
  const safeCount = completed.filter((r) => r.recommendation === 'SAFE').length;

  // ── Build the insight list ─────────────────────────────────────────────
  const insights: Insight[] = [];

  if (totalLines > 0) {
    insights.push({
      id: 'lines',
      label: 'Lines Analyzed',
      value: fmt(totalLines),
      sub: totalFiles > 0 ? `across ${fmt(totalFiles)} files` : undefined,
      color: 'text-blue-400',
    });
  }

  if (totalFindings > 0) {
    const subPart =
      criticalCount > 0
        ? `${criticalCount} critical`
        : highCount > 0
          ? `${highCount} high`
          : undefined;
    insights.push({
      id: 'findings',
      label: 'Issues Caught',
      value: fmt(totalFindings),
      sub: subPart,
      color:
        criticalCount > 0 ? 'text-red-400' : highCount > 0 ? 'text-orange-400' : 'text-yellow-400',
    });
  }

  if (topCategory) {
    const label = topCategory[0].replace(/_/g, ' ');
    insights.push({
      id: 'topCategory',
      label: 'Top Issue Type',
      value: label.charAt(0).toUpperCase() + label.slice(1),
      sub: `${topCategory[1]} occurrence${topCategory[1] !== 1 ? 's' : ''}`,
      color: 'text-amber-400',
    });
  }

  if (uniqueTargets > 0) {
    insights.push({
      id: 'repos',
      label: 'Repos Scanned',
      value: String(uniqueTargets),
      sub: uniqueTargetsThisMonth > 0 ? `${uniqueTargetsThisMonth} this month` : 'unique targets',
      color: 'text-green-400',
    });
  }

  if (safeCount > 0) {
    const pct = Math.round((safeCount / completed.length) * 100);
    insights.push({
      id: 'safeRate',
      label: 'Clean Scans',
      value: `${pct}%`,
      sub: `${safeCount} of ${completed.length} passed`,
      color: 'text-emerald-400',
    });
  }

  if (riskiest) {
    const score = (riskiest.scores as Record<string, unknown>).aivss as number;
    insights.push({
      id: 'riskiest',
      label: 'Riskiest Target',
      value: `${score.toFixed(1)}/10`,
      sub: repoName(riskiest.target),
      color: score >= 7 ? 'text-red-400' : 'text-orange-400',
    });
  }

  if (safestScored) {
    const score = (safestScored.scores as Record<string, unknown>).aivss as number;
    insights.push({
      id: 'safest',
      label: 'Best Score',
      value: `${score.toFixed(1)}/10`,
      sub: repoName(safestScored.target),
      color: 'text-teal-400',
    });
  }

  if (topFile) {
    const filename = topFile[0].split('/').pop() ?? topFile[0];
    insights.push({
      id: 'hotspot',
      label: 'Issue Hotspot',
      value: trunc(filename),
      sub: `${topFile[1]} finding${topFile[1] !== 1 ? 's' : ''}`,
      color: 'text-purple-400',
    });
  }

  if (avgDuration > 0) {
    const secs = avgDuration / 1000;
    insights.push({
      id: 'duration',
      label: 'Avg Scan Time',
      value: secs >= 60 ? `${(secs / 60).toFixed(1)}m` : `${secs.toFixed(0)}s`,
      sub: 'per scan',
      color: 'text-slate-300',
    });
  }

  if (hardcodedIPCount > 0) {
    insights.push({
      id: 'hardcodedIPs',
      label: 'Hardcoded IPs',
      value: String(hardcodedIPCount),
      sub: `repo${hardcodedIPCount !== 1 ? 's' : ''} flagged`,
      color: 'text-red-400',
    });
  }

  if (topDangerousCall) {
    const call = topDangerousCall[0];
    insights.push({
      id: 'dangerousCall',
      label: 'Top Dangerous Call',
      value: trunc(call),
      sub: `${topDangerousCall[1]}× across scans`,
      color: 'text-orange-400',
    });
  }

  if (avgOpenssf !== null) {
    insights.push({
      id: 'openssf',
      label: 'Avg OpenSSF Score',
      value: avgOpenssf.toFixed(1),
      sub: `out of 10`,
      color:
        avgOpenssf >= 7 ? 'text-green-400' : avgOpenssf >= 4 ? 'text-yellow-400' : 'text-red-400',
    });
  }

  if (topTypeEntry) {
    const typeLabel = TARGET_TYPE_LABELS[topTypeEntry[0]] ?? topTypeEntry[0];
    insights.push({
      id: 'topType',
      label: 'Most Scanned',
      value: typeLabel,
      sub: `${topTypeEntry[1]} scan${topTypeEntry[1] !== 1 ? 's' : ''}`,
      color: 'text-cyan-400',
    });
  }

  return insights;
}
